import React, { useEffect, useRef, useState } from 'react';
import PlotTrackAddScene from './PlotTrackAddScene';
import PlotTrackCell from './PlotTrackCell';
import SceneCardMenu from './SceneCardMenu';
import PlotTrackWhenCell from './PlotTrackWhenCell';
import {
  clampWidth, columnWidthPx, widthStyle, WIDTH_MAX, WIDTH_MIN, WIDTH_STEP,
} from '../../utils/plottrackWidth';
import PlotTrackHeaderSpan from './PlotTrackHeaderSpan';
import {
  cellAt,
  cellsForColumn,
  CHEKHOV_VISIBILITIES,
  chekhovVisibilityCopy,
  colorInputValue,
  columnDisplayName,
  columnIsSequence,
  headerSpan,
  isChekhovRow,
  linePosition,
  rowCanPickColor,
  rowLabel,
  whoseHeadReadout,
} from '../../utils/plottrackGrid';
import { outlineActHeader } from '../../utils/outlineActHeader';
import HelpIcon from '../Common/HelpIcon';
import {
  outlineGridColumns,
  outlineParts,
  outsideSequenceHint,
} from '../../utils/outlineCardRows';
import {
  ADD_SCENE_PLUS,
  CHEKHOV_HOW_CLEAR,
  EDIT_ACT,
  EDIT_CHAPTER,
  EDIT_SEQUENCE,
  GRAIN_SEQUENCE,
  NAME_THIS_THROUGH_LINE,
  OUTLINE_NOT_PLANNED,
  PLOTTRACK_NO_ROWS,
  PT_PEOPLE_MORE,
  PT_WIDTH_HANDLE_ARIA,
  PT_WHEN_ROW_LABEL,
  RENAME_THROUGH_LINE,
  SAVE_THROUGH_LINE_NAME,
} from '../../utils/writerCopy';

// #2049: rows keep a fixed taller minimum instead of dividing the window.
// 112px leaves three text lines after the 54px cell chrome (19px each),
// above the two-line floor (92px). Shorter heights still drop the beat
// count and keep two lines (the old tight rule).
export const ROW_HEIGHT_PX = 112;
const CELL_CHROME_PX = 54;
const CELL_CHROME_TIGHT_PX = 34;
const CELL_LINE_PX = 19;

// #2054 compact. 64px is the row minimum that still holds a 12px state
// line (15px with 1.25 leading) and one 13px beat line (17px at 1.3)
// after the table cell's 14px chrome and the cell's own 4px padding.
// 100px scene columns are narrower than the 140px floor, so six scenes
// fit a laptop grid. Comfortable stays the default.
export const COMPACT_ROW_HEIGHT_PX = 64;
export const COMPACT_SCENE_COLUMN_PX = 100;
export const GRID_ROW_HEAD_PX = 200;
// Wide enough for "+ Add a scene" on one line. Matches .pt-grid__add.
export const ADD_COLUMN_PX = 188;
// Act / Sequence / Chapter band: 6+6 padding, 1+1 border, 32px button.
export const GRID_HEADER_BAND_PX = 46;
// Compact Scenes header (#2263): the menu has its own row (44px) so it
// does not cover the title. Two name lines, optional grain, whose-head,
// and people. 130px is that stack plus the cell's padding and borders.
export const COMPACT_SCENE_HEADER_PX = 130;
export const COMPACT_CELL_CHROME_PX = 14;
export const COMPACT_CELL_PAD_Y_PX = 8;
export const COMPACT_CELL_GAP_PX = 2;
export const COMPACT_STATE_LINE_PX = 15;
export const COMPACT_BEAT_LINE_PX = 17;
// Outline room at app zoom 100%. Tools and session bars are off on this
// route. Chat is the 48px strip when folded, 350px when open.
export const LAPTOP_GRID_CHROME = {
  sidebar: 300,
  mainBorder: 1,
  gridPadX: 48,
  outlineHeader: 64,
  gridPadY: 16,
};
export const LAPTOP_VIEWPORTS = [
  { name: '1280×800, chat folded', width: 1280, height: 800, chat: 48 },
  { name: '1366×768, chat folded', width: 1366, height: 768, chat: 48 },
  { name: '1536×864, chat open', width: 1536, height: 864, chat: 350 },
];

export function gridBudget(viewport, chrome = LAPTOP_GRID_CHROME) {
  return {
    width: viewport.width - chrome.sidebar - chrome.mainBorder - viewport.chat - chrome.gridPadX,
    height: viewport.height - chrome.outlineHeader - chrome.gridPadY,
  };
}

export function compactGridExtent(scenes = 6, rows = 6) {
  return {
    width: GRID_ROW_HEAD_PX + scenes * COMPACT_SCENE_COLUMN_PX,
    height: GRID_HEADER_BAND_PX * 3 + COMPACT_SCENE_HEADER_PX + rows * COMPACT_ROW_HEIGHT_PX,
  };
}

export function compactGridFits(viewport) {
  const budget = gridBudget(viewport);
  const extent = compactGridExtent();
  return extent.width <= budget.width && extent.height <= budget.height;
}

/** True when the compact row box can show the state word and one beat line. */
export function compactCellShowsBeatAndState() {
  const content = COMPACT_ROW_HEIGHT_PX
    - COMPACT_CELL_CHROME_PX
    - COMPACT_CELL_PAD_Y_PX
    - COMPACT_CELL_GAP_PX;
  return content >= COMPACT_STATE_LINE_PX + COMPACT_BEAT_LINE_PX;
}

export function gridSizeStyle(size = 'comfortable') {
  if (size === 'compact') {
    return {
      tight: true,
      columnPx: COMPACT_SCENE_COLUMN_PX,
      style: {
        '--pt-row-h': `${COMPACT_ROW_HEIGHT_PX}px`,
        '--pt-cell-lines': '1',
      },
    };
  }
  return { ...gridRowStyle(ROW_HEIGHT_PX), columnPx: null };
}

export function gridRowStyle(rowPx = ROW_HEIGHT_PX) {
  const linesWithCount = Math.floor((rowPx - CELL_CHROME_PX) / CELL_LINE_PX);
  const tight = linesWithCount < 2;
  const lines = Math.max(
    tight ? 2 : 1,
    tight ? Math.floor((rowPx - CELL_CHROME_TIGHT_PX) / CELL_LINE_PX) : linesWithCount,
  );
  return {
    tight,
    style: {
      '--pt-row-h': `${rowPx}px`,
      '--pt-cell-lines': String(lines),
    },
  };
}

export function RowColorControl({ row, onChangeColor, showName = true }) {
  const stored = colorInputValue(row?.color);
  const [draft, setDraft] = useState(stored);
  // #2052: a reload never blurs, so the picker must commit on change.
  // lastSent keeps that change and the following blur to one PATCH.
  const lastSent = useRef(stored.toLowerCase());
  const display = rowLabel(row);

  useEffect(() => {
    setDraft(stored);
    lastSent.current = stored.toLowerCase();
  }, [stored, row?.id]);

  if (!rowCanPickColor(row) || !onChangeColor) return null;

  const commit = (event) => {
    const next = colorInputValue(event.target.value);
    const key = next.toLowerCase();
    if (key === stored.toLowerCase()) return;
    if (key === lastSent.current) return;
    lastSent.current = key;
    onChangeColor(row.id, next);
  };

  return (
    <label className="pt-row-color">
      {showName ? <span className="pt-row-color__name">{display}</span> : null}
      <input
        type="color"
        value={draft}
        aria-label={`${display}: Color`}
        onChange={(event) => {
          setDraft(event.target.value);
          commit(event);
        }}
        onBlur={commit}
      />
    </label>
  );
}

function RowHead({ row, onRename, onChangeVisibility, onChangeColor }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(rowLabel(row));
  const display = rowLabel(row);

  const saveName = async () => {
    const next = name.trim();
    if (!next || next === display) {
      setEditing(false);
      setName(display);
      return;
    }
    if (onRename) await onRename(row.id, next);
    setEditing(false);
  };

  return (
    <th
      scope="row"
      className="pt-grid__row-head"
      style={{ '--pt-row-color': row.color }}
      aria-label={display}
    >
      {rowCanPickColor(row) && onChangeColor ? (
        <RowColorControl row={row} onChangeColor={onChangeColor} showName={false} />
      ) : (
        <span className="pt-grid__swatch" aria-hidden="true" />
      )}
      {editing ? (
        <span className="pt-grid__rename">
          <input
            value={name}
            onChange={(event) => setName(event.target.value)}
            aria-label={NAME_THIS_THROUGH_LINE}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                saveName();
              }
              if (event.key === 'Escape') {
                setEditing(false);
                setName(display);
              }
            }}
          />
          <button type="button" onClick={saveName}>{SAVE_THROUGH_LINE_NAME}</button>
        </span>
      ) : (
        <span className="pt-grid__row-name-wrap">
          <span className="pt-grid__row-name" title={display}>{display}</span>
          {onRename && (
            <button
              type="button"
              className="pt-grid__rename-btn"
              onClick={() => {
                setName(display);
                setEditing(true);
              }}
            >
              {RENAME_THROUGH_LINE}
            </button>
          )}
        </span>
      )}
      {isChekhovRow(row) && (
        onChangeVisibility ? (
          <label className="pt-grid__visibility">
            <span className="pt-grid__visibility-label">{CHEKHOV_HOW_CLEAR}</span>
            <select
              value={row.visibility || 'hidden'}
              aria-label={`${display}: ${CHEKHOV_HOW_CLEAR}`}
              onChange={(event) => onChangeVisibility(row.id, event.target.value)}
            >
              {CHEKHOV_VISIBILITIES.map((value) => {
                const copy = chekhovVisibilityCopy(value);
                return (
                  <option key={value} value={value}>
                    {copy.label}
                  </option>
                );
              })}
            </select>
          </label>
        ) : (
          <span className="pt-grid__visibility">
            {chekhovVisibilityCopy(row.visibility).label}
          </span>
        )
      )}
    </th>
  );
}

// One scene column's resize handle (spec §3): drag the edge, or arrow keys when focused.
// Hidden in High Contrast (no drag there; the Column width control still works).
function ResizeHandle({ sceneName, width, onDrag, onCommit }) {
  const startDrag = (event) => {
    const head = event.currentTarget.parentElement;
    const startX = event.clientX;
    const startWidth = width || head.getBoundingClientRect().width;
    let last = startWidth;
    const move = (e) => { last = clampWidth(startWidth + e.clientX - startX); onDrag(last); };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      onCommit(last);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    event.preventDefault();
  };
  const onKeyDown = (event) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    event.preventDefault();
    const head = event.currentTarget.parentElement;
    const base = width || head.getBoundingClientRect().width || WIDTH_MIN;
    onCommit(clampWidth(base + (event.key === 'ArrowRight' ? WIDTH_STEP : -WIDTH_STEP)));
  };
  return (
    <span
      role="separator"
      aria-orientation="vertical"
      aria-label={PT_WIDTH_HANDLE_ARIA(sceneName)}
      aria-valuemin={WIDTH_MIN}
      aria-valuemax={WIDTH_MAX}
      aria-valuenow={width || undefined}
      tabIndex={0}
      className="pt-col-resize"
      onPointerDown={startDrag}
      onKeyDown={onKeyDown}
    />
  );
}

// "Marcus Kane / Julian Ashford" -> Marcus; "The Dead Men (Kane's team)" -> The Dead Men.
function chipName(name) {
  const first = String(name || '').split('/')[0].replace(/\([^)]*\)/g, ' ').replace(/["“”]/g, ' ').trim();
  if (/^(the|a|an)\s/i.test(first)) return first.replace(/\s+/g, ' ');
  return first.split(/\s+/)[0] || String(name || '');
}

// People named in the scene (spec §2), shortest form: first names, three then "+N".
function SceneChips({ people }) {
  const list = people || [];
  if (list.length === 0) return null;
  const shown = list.slice(0, 3).map((person) => chipName(person.name));
  return (
    <span className="pt-grid__people" title={list.map((person) => chipName(person.name)).join(' · ')}>
      {shown.join(' · ')}
      {list.length > 3 ? ` ${PT_PEOPLE_MORE(list.length - 3)}` : ''}
    </span>
  );
}

function HeaderRow({
  label,
  items,
  columns,
  lineColumns,
  className,
  formatLabel,
  kind,
  editingId,
  onStartEdit,
  onSaveSpan,
  onCancelEdit,
  laneSplit = false,
  padEnd = false,
}) {
  if (!items || items.length === 0) return null;
  if (kind === 'sequence' && items.every((item) => /^sequence\s*\d+$/i.test(String(item?.display_name || '').trim()))) {
    return null;
  }
  const used = new Set();
  const cells = [];
  const pickerColumns = lineColumns || columns;
  columns.forEach((column, index) => {
    if (used.has(String(column.scene_id))) return;
    const header = items.find((item) => (item.scene_ids || []).some((id) => String(id) === String(column.scene_id)));
    if (!header) {
      cells.push(<td key={`gap-${index}`} className="pt-grid__header-gap" />);
      return;
    }
    const ids = new Set((header.scene_ids || []).map((id) => String(id)));
    let span = headerSpan(header, columns);
    if (laneSplit) {
      span = 0;
      for (let i = index; i < columns.length; i += 1) {
        if (!ids.has(String(columns[i].scene_id))) break;
        span += 1;
      }
      columns.slice(index, index + span).forEach((item) => used.add(String(item.scene_id)));
    } else {
      ids.forEach((id) => used.add(id));
    }
    const display = (formatLabel ? formatLabel(header.display_name) : header.display_name) || label;
    const canEdit = Boolean(onStartEdit) && !header.emptyLane;
    const editing = canEdit && String(editingId) === String(header.id);
    cells.push(
      <th key={header.id || `h-${index}`} colSpan={Math.max(span, 1)} scope="colgroup">
        {editing ? (
          <PlotTrackHeaderSpan
            kind={kind}
            header={header}
            columns={pickerColumns}
            onSave={onSaveSpan}
            onCancel={onCancelEdit}
          />
        ) : canEdit ? (
          <button
            type="button"
            className="pt-grid__header-edit"
            onClick={() => onStartEdit(header.id)}
            aria-label={`${kind === 'act' ? EDIT_ACT : kind === 'chapter' ? EDIT_CHAPTER : EDIT_SEQUENCE}: ${display}`}
          >
            {display}
          </button>
        ) : (
          display
        )}
      </th>,
    );
  });
  return (
    <tr className={className}>
      <th scope="row">{label}</th>
      {cells}
      {padEnd && <td className="pt-grid__header-gap" />}
    </tr>
  );
}

// The same words already inside PLOTTRACK_NO_ROWS. Not a new label.
const GENERATE_FROM_BOOK = 'Generate to start from this book';

function EmptyStorylines({ colSpan, onOpenBuild }) {
  const at = PLOTTRACK_NO_ROWS.indexOf(GENERATE_FROM_BOOK);
  if (!onOpenBuild || at < 0) {
    return (
      <td colSpan={colSpan} className="pt-grid__empty">
        {PLOTTRACK_NO_ROWS}
        <HelpIcon hintKey="B4" />
      </td>
    );
  }
  return (
    <td colSpan={colSpan} className="pt-grid__empty">
      {PLOTTRACK_NO_ROWS.slice(0, at)}
      <button type="button" className="pt-toolbar__btn" onClick={onOpenBuild}>
        {GENERATE_FROM_BOOK}
      </button>
      {PLOTTRACK_NO_ROWS.slice(at + GENERATE_FROM_BOOK.length)}
      <HelpIcon hintKey="B4" />
    </td>
  );
}

export default function PlotTrackGrid({
  rows,
  columns,
  lineColumns,
  cells,
  headers,
  unplannedSequences = [],
  onOpenBuild,
  selected,
  onSelectCell,
  onRenameRow,
  onChangeVisibility,
  onChangeColor,
  onEditHeader,
  showWhen = false,
  whenLabels = [],
  onSetWhen,
  onCreateWhen,
  columnWidth = 'standard',
  onResizeColumn,
  onRenameScene,
  onMoveScene,
  onStartChapter,
  onChatScene,
  onDeleteScene,
  onOpenScene,
  onAddScene,
  onSuggestTitle,
  frameworkNames = [],
  frameworkApplied = false,
  storyId = '',
  size = 'comfortable',
}) {
  // Width while dragging, before it's saved (spec §3).
  // Compact is a display width. It does not write the book's column widths.
  const [dragPx, setDragPx] = useState({});
  const compact = size === 'compact';
  const { tight, style: fillStyle, columnPx } = gridSizeStyle(compact ? 'compact' : 'comfortable');
  const pxFor = (column) => {
    if (dragPx[column.id] != null) return dragPx[column.id];
    if (compact) return columnPx;
    return columnWidthPx(column, columnWidth);
  };
  // #2321: empty sequences stay as lanes after the book has scenes.
  const display = outlineGridColumns({ columns, headers, frameworkNames });
  const gridColumns = display.columns;
  const gridHeaders = display.headers || headers;
  const openSequences = columns.length === 0
    ? (unplannedSequences || []).map((name) => String(name || '').trim()).filter(Boolean)
    : [];
  const showAdd = Boolean(onAddScene) && columns.length > 0;
  const laneCount = gridColumns.length || openSequences.length;
  const tableWidth = compact
    ? GRID_ROW_HEAD_PX + laneCount * columnPx + (showAdd ? ADD_COLUMN_PX : 0)
    : null;
  const acts = gridHeaders?.acts || [];
  const sequences = headers?.sequences || [];
  const sceneTitles = {};
  (columns || []).forEach((column) => {
    if (!column?.scene_id) return;
    sceneTitles[String(column.scene_id)] = columnDisplayName(column, cellsForColumn(column, cells));
  });
  const headerSequences = gridHeaders?.sequences || [];
  const chapters = gridHeaders?.chapters || [];
  const [editingId, setEditingId] = useState(null);
  const pickerColumns = lineColumns || columns;
  const outsideHints = new Map();
  if (frameworkApplied || (frameworkNames || []).length > 0) {
    outlineParts({ columns, headers, frameworkNames }).forEach((part) => {
      part.scenes.forEach((column) => {
        const name = columnDisplayName(column, cellsForColumn(column, cells));
        const hint = outsideSequenceHint(part, true, name);
        if (hint && column?.scene_id) outsideHints.set(String(column.scene_id), hint);
      });
    });
  }
  const lastSceneId = [...columns].reverse().find((column) => column?.scene_id)?.scene_id;

  const saveSpan = async (payload) => {
    if (onEditHeader) await onEditHeader(payload);
    setEditingId(null);
  };

  return (
    <div className="pt-grid-wrap">
      <table
        className={`pt-grid${tight ? ' pt-grid--tight' : ''}${compact ? ' pt-grid--compact' : ''}`}
        aria-label="Plot Grid grid"
        style={tableWidth ? { ...fillStyle, width: `${tableWidth}px` } : fillStyle}
      >
        {compact && (
          <colgroup>
            <col style={{ width: `${GRID_ROW_HEAD_PX}px` }} />
            {gridColumns.map((column) => (
              <col key={column.id} style={{ width: `${pxFor(column)}px` }} />
            ))}
            {openSequences.map((name, index) => (
              <col key={`open-${index}`} style={{ width: `${columnPx}px` }} />
            ))}
            {showAdd && <col style={{ width: `${ADD_COLUMN_PX}px` }} />}
          </colgroup>
        )}
        <thead>
          <HeaderRow
            label="Act"
            items={acts}
            columns={gridColumns}
            lineColumns={pickerColumns}
            className="pt-grid__acts"
            formatLabel={outlineActHeader}
            kind="act"
            laneSplit={display.inserted}
            padEnd={showAdd}
            editingId={editingId}
            onStartEdit={onEditHeader ? setEditingId : undefined}
            onSaveSpan={saveSpan}
            onCancelEdit={() => setEditingId(null)}
          />
          {openSequences.length === 0 && (
            <HeaderRow
              label="Sequence"
              items={headerSequences}
              columns={gridColumns}
              lineColumns={pickerColumns}
              className="pt-grid__sequences"
              kind="sequence"
              laneSplit={display.inserted}
              padEnd={showAdd}
              editingId={editingId}
              onStartEdit={onEditHeader ? setEditingId : undefined}
              onSaveSpan={saveSpan}
              onCancelEdit={() => setEditingId(null)}
            />
          )}
          {openSequences.length > 0 && (
            <tr className="pt-grid__sequences">
              <th scope="row">Sequence</th>
              {openSequences.map((name, index) => (
                <th key={`seq-${index}`} scope="colgroup">{name}</th>
              ))}
            </tr>
          )}
          <HeaderRow
            label="Chapter"
            items={chapters}
            columns={gridColumns}
            lineColumns={pickerColumns}
            className="pt-grid__chapters"
            kind="chapter"
            laneSplit={display.inserted}
            padEnd={showAdd}
            editingId={editingId}
            onStartEdit={onEditHeader ? setEditingId : undefined}
            onSaveSpan={saveSpan}
            onCancelEdit={() => setEditingId(null)}
          />
          {showWhen && columns.length > 0 && (
            <tr className="pt-grid__when">
              <th scope="row">{PT_WHEN_ROW_LABEL}</th>
              {gridColumns.map((column, index) => (
                column.emptyLane ? (
                  <th key={`when-${column.id}`} scope="col" className="pt-grid__lane" />
                ) : (
                  <th key={`when-${column.id}`} scope="col" style={widthStyle(pxFor(column))}>
                    <PlotTrackWhenCell
                      sceneName={columnDisplayName(column, cellsForColumn(column, cells)) || `${index + 1}`}
                      value={column.when?.id || ''}
                      offer={column.when_offer}
                      labels={whenLabels}
                      onSet={(labelId) => onSetWhen && onSetWhen(column.scene_id, labelId)}
                      onCreate={(label) => onCreateWhen && onCreateWhen(label)}
                    />
                  </th>
                )
              ))}
              {showAdd && <th scope="col" className="pt-grid__header-gap" />}
            </tr>
          )}
        </thead>
        <tbody>
          {/* Scenes is the first body row, not a thead row. Sticky cells are
              contained by their row group; the last row of a multi-row thead
              has no room to pin, so the header block scrolled away together.
              Act / Sequence / Chapter stay in thead and are not sticky. */}
          <tr className="pt-grid__scenes">
            <th scope="row">Scenes</th>
            {columns.length === 0 && openSequences.length === 0 && (
              <th scope="col" className="pt-grid__empty-header">No scenes on the line yet</th>
            )}
            {openSequences.map((name, index) => (
              <th key={`plan-${index}`} scope="col" className="pt-grid__empty-header">
                {OUTLINE_NOT_PLANNED}
              </th>
            ))}
            {gridColumns.map((column, index) => {
              if (column.emptyLane) {
                return (
                  <th key={column.id} scope="col" className="pt-grid__empty-header pt-grid__lane">
                    {OUTLINE_NOT_PLANNED}
                  </th>
                );
              }
              const position = linePosition(pickerColumns, column.scene_id);
              const ordinal = position >= 0 ? position : index;
              const related = cellsForColumn(column, cells);
              const sequence = columnIsSequence(column, related);
              const sceneName = columnDisplayName(column, related);
              const firstRow = rows[0];
              const grainWord = sequence ? 'sequence' : 'scene';
              const openScene = () => {
                if (onOpenScene) {
                  onOpenScene(column);
                  return;
                }
                if (firstRow && onSelectCell) {
                  onSelectCell(firstRow, column, cellAt(cells, firstRow.id, column.id));
                }
              };
              const canOpen = Boolean(onOpenScene || (firstRow && onSelectCell));
              const headClick = (event) => {
                const target = event.target;
                if (target?.closest?.('.pt-scene-menu, .pt-col-resize, .pt-grid__scene-select, button, a, input, select, textarea')) {
                  return;
                }
                openScene();
              };
              const titleKey = (event) => {
                if (event.key !== 'Enter') return;
                event.preventDefault();
                openScene();
              };
              const sceneBody = (
                <>
                  {sequence && <span className="pt-grid__grain">{GRAIN_SEQUENCE}</span>}
                  <span className="pt-grid__scene-name" title={sceneName}>{sceneName}</span>
                  <span className="pt-grid__whose-head">
                    {whoseHeadReadout(column) || '—'}
                  </span>
                  <SceneChips people={column.people} />
                </>
              );
              return (
                <th
                  key={column.id}
                  scope="col"
                  data-grain={grainWord}
                  className="pt-grid__scene-head"
                  style={{
                    ...widthStyle(pxFor(column)),
                    borderLeft: `3px solid ${(rows.find((row) => row.type === 'main') || rows[0])?.color || 'var(--accent)'}`,
                  }}
                  onClick={canOpen ? headClick : undefined}
                >
                  <div className="pt-grid__scene-top">
                    <span className="pt-grid__scene-ordinal">{ordinal + 1}</span>
                    {column.scene_id && (
                      <SceneCardMenu
                        sceneName={sceneName}
                        sceneId={column.scene_id}
                        sequences={sequences}
                        titles={sceneTitles}
                        canStartChapter={linePosition(pickerColumns, column.scene_id) > 0}
                        onOpen={canOpen ? openScene : undefined}
                        onRename={onRenameScene
                          ? (title) => onRenameScene(column.scene_id, title)
                          : undefined}
                        onMove={onMoveScene
                          ? (place) => onMoveScene(column.scene_id, place)
                          : undefined}
                        onStartChapter={onStartChapter ? () => {
                          const lineIndex = linePosition(pickerColumns, column.scene_id);
                          const previous = lineIndex > 0 ? pickerColumns[lineIndex - 1] : null;
                          if (previous?.scene_id) onStartChapter(previous.scene_id);
                        } : undefined}
                        onChat={onChatScene ? () => onChatScene(sceneName, column.scene_id) : undefined}
                        onDelete={onDeleteScene ? () => onDeleteScene(column.scene_id) : undefined}
                      />
                    )}
                    {canOpen ? (
                      <button
                        type="button"
                        className="pt-grid__scene-select"
                        onClick={(event) => {
                          event.stopPropagation();
                          openScene();
                        }}
                        onKeyDown={titleKey}
                        aria-label={`Select ${grainWord} ${sceneName}`}
                      >
                        {sceneBody}
                      </button>
                    ) : (
                      <div className="pt-grid__scene-select">
                        {sceneBody}
                      </div>
                    )}
                  </div>
                  {onResizeColumn && !compact && (
                    <ResizeHandle
                      sceneName={sceneName}
                      width={pxFor(column)}
                      onDrag={(px) => setDragPx((prev) => ({ ...prev, [column.id]: px }))}
                      onCommit={(px) => {
                        setDragPx((prev) => { const next = { ...prev }; delete next[column.id]; return next; });
                        onResizeColumn(column.id, px);
                      }}
                    />
                  )}
                  {outsideHints.get(String(column.scene_id)) && (
                    <p className="pt-grid__outside">{outsideHints.get(String(column.scene_id))}</p>
                  )}
                </th>
              );
            })}
            {showAdd && (
              <th scope="col" className="pt-grid__add">
                <article className="pt-scene-card pt-scene-card--add">
                  <PlotTrackAddScene
                    idPrefix="pt-add-grid"
                    draftKey={storyId ? `add:${storyId}:grid` : ''}
                    buttonLabel={ADD_SCENE_PLUS}
                    columns={pickerColumns}
                    cells={cells}
                    sequences={sequences}
                    defaultAfterSceneId={lastSceneId}
                    onAdd={onAddScene}
                    onSuggestTitle={onSuggestTitle}
                  />
                </article>
              </th>
            )}
          </tr>
          {rows.length === 0 && (
            <tr>
              <EmptyStorylines
                colSpan={Math.max(laneCount + (showAdd ? 1 : 0) + 1, 2)}
                onOpenBuild={onOpenBuild}
              />
            </tr>
          )}
          {rows.map((row) => (
            <tr
              key={row.id}
              className={`pt-grid__row pt-grid__row--${row.type}${row.type === 'main' ? ' pt-grid__row--pinned' : ''}`}
            >
              <RowHead
                row={row}
                onRename={onRenameRow}
                onChangeVisibility={onChangeVisibility}
                onChangeColor={onChangeColor}
              />
              {columns.length === 0 && openSequences.length === 0 && <td className="pt-grid__empty-header" />}
              {openSequences.map((name, index) => (
                <td key={`open-cell-${row.id}-${index}`} className="pt-grid__empty-header" />
              ))}
              {gridColumns.map((column) => {
                if (column.emptyLane) {
                  return <td key={`${row.id}:${column.id}`} className="pt-grid__empty-header pt-grid__lane" />;
                }
                const cell = cellAt(cells, row.id, column.id);
                const selectedHere = selected
                  && String(selected.row?.id) === String(row.id)
                  && String(selected.column?.id) === String(column.id);
                return (
                  <td key={`${row.id}:${column.id}`} style={widthStyle(pxFor(column))}>
                    <PlotTrackCell
                      cell={cell}
                      row={row}
                      column={column}
                      selected={selectedHere}
                      onSelect={onSelectCell}
                    />
                  </td>
                );
              })}
              {showAdd && <td className="pt-grid__add-gap" />}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
